/**
 * Export Service - Export objects to various formats
 */

import { dataRepository } from '../repository/DataRepository';
import type { PKMObject, Block, Property } from '../models/types';

interface ExportOptions {
  includeProperties?: boolean;
  includeBlocks?: boolean;
  includeTags?: boolean;
  includeRelationships?: boolean;
}

const DEFAULT_OPTIONS: ExportOptions = {
  includeProperties: true,
  includeBlocks: true,
  includeTags: true,
  includeRelationships: true,
};

/**
 * Convert a single object to Markdown with YAML frontmatter
 */
export async function objectToMarkdown(
  objectId: string,
  options: ExportOptions = DEFAULT_OPTIONS
): Promise<string> {
  const object = await dataRepository.getObject(objectId);
  if (!object) {
    throw new Error(`Object not found: ${objectId}`);
  }

  const blocks = options.includeBlocks
    ? await dataRepository.getBlocksForObject(objectId)
    : [];

  const properties = options.includeProperties
    ? await dataRepository.getPropertiesForObject(objectId)
    : [];

  return formatObjectAsMarkdown(object, blocks, properties, options);
}

/**
 * Export multiple objects to a zip file
 */
export async function exportObjectsToZip(
  objectIds: string[],
  options: ExportOptions = DEFAULT_OPTIONS
): Promise<Blob> {
  // Dynamic import for JSZip (only when needed)
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  for (const objectId of objectIds) {
    try {
      const markdown = await objectToMarkdown(objectId, options);
      const object = await dataRepository.getObjectById(objectId);
      if (object) {
        const fileName = sanitizeFileName(object.name || 'untitled') + '.md';
        const folder = object.type;
        zip.file(`${folder}/${fileName}`, markdown);
      }
    } catch (error) {
      console.error(`Failed to export object ${objectId}:`, error);
    }
  }

  return zip.generateAsync({ type: 'blob' });
}

/**
 * Export all objects to a zip file
 */
export async function exportAllToZip(
  options: ExportOptions = DEFAULT_OPTIONS
): Promise<Blob> {
  const allObjects = await dataRepository.getAllObjects();
  const objectIds = allObjects.map(o => o.id);
  return exportObjectsToZip(objectIds, options);
}

/**
 * Format an object as Markdown with YAML frontmatter
 */
function formatObjectAsMarkdown(
  object: PKMObject,
  blocks: Block[],
  properties: Property[],
  options: ExportOptions
): string {
  const lines: string[] = [];

  // YAML Frontmatter
  lines.push('---');
  lines.push(`id: ${object.id}`);
  lines.push(`type: ${object.type}`);
  lines.push(`created: ${object.createdAt}`);
  lines.push(`updated: ${object.updatedAt}`);

  // Add properties
  if (options.includeProperties && properties.length > 0) {
    for (const prop of properties) {
      if (prop.value !== undefined && prop.value !== null && prop.value !== '') {
        const yamlValue = formatYamlValue(prop.value, prop.type);
        lines.push(`${prop.name}: ${yamlValue}`);
      }
    }
  }

  // Add tags
  if (options.includeTags) {
    const tagsProp = properties.find(p => p.name === 'tags');
    if (tagsProp?.value) {
      try {
        const tags = JSON.parse(tagsProp.value);
        if (Array.isArray(tags) && tags.length > 0) {
          lines.push(`tags: [${tags.map(t => `"${t}"`).join(', ')}]`);
        }
      } catch {
        // tags might already be formatted or invalid
      }
    }
  }

  lines.push('---');
  lines.push('');

  // Title
  lines.push(`# ${object.name || 'Untitled'}`);
  lines.push('');

  // Content from blocks
  if (options.includeBlocks && blocks.length > 0) {
    // Sort blocks by position (top to bottom, left to right)
    const sortedBlocks = [...blocks].sort((a, b) => {
      const yDiff = a.y - b.y;
      if (Math.abs(yDiff) < 50) {
        return a.x - b.x;
      }
      return yDiff;
    });

    for (const block of sortedBlocks) {
      const blockContent = formatBlockAsMarkdown(block);
      if (blockContent) {
        lines.push(blockContent);
        lines.push('');
      }
    }
  }

  // Legacy content field
  if (object.content && typeof object.content === 'string' && object.content.trim()) {
    lines.push(object.content);
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Format a block as Markdown
 */
function formatBlockAsMarkdown(block: Block): string {
  const content = block.content || '';

  switch (block.type) {
    case 'text':
      return content;

    case 'heading':
      const level = block.metadata?.level || 2;
      const prefix = '#'.repeat(Math.min(level, 6));
      return `${prefix} ${content}`;

    case 'list':
      return content
        .split('\n')
        .map(line => `- ${line.trim()}`)
        .join('\n');

    case 'checklist':
    case 'tasklist':
      return content
        .split('\n')
        .map(line => {
          const checked = line.startsWith('[x]') || line.startsWith('[X]');
          const text = line.replace(/^\[[ xX]\]\s*/, '').trim();
          return checked ? `- [x] ${text}` : `- [ ] ${text}`;
        })
        .join('\n');

    case 'code':
      const language = block.metadata?.language || '';
      return `\`\`\`${language}\n${content}\n\`\`\``;

    case 'quote':
      return content
        .split('\n')
        .map(line => `> ${line}`)
        .join('\n');

    case 'image':
      const imagePath = block.metadata?.imagePath || content;
      const altText = block.metadata?.altText || 'Image';
      return `![${altText}](${imagePath})`;

    case 'file':
      const fileName = block.metadata?.fileName || 'Attachment';
      const filePath = block.metadata?.filePath || content;
      return `[${fileName}](${filePath})`;

    case 'embed':
      return `[Embed: ${content}](${content})`;

    case 'audio':
      return `[Audio: ${block.metadata?.fileName || content}](${content})`;

    case 'divider':
      return '---';

    case 'table':
      // Try to parse table data from content or metadata
      if (block.metadata?.tableData) {
        return formatTableAsMarkdown(block.metadata.tableData);
      }
      return content;

    case 'widget':
      return `<!-- Widget: ${block.metadata?.widgetType || 'unknown'} -->`;

    default:
      return content;
  }
}

/**
 * Format table data as Markdown
 */
function formatTableAsMarkdown(tableData: any): string {
  if (!tableData?.headers || !tableData?.rows) {
    return '';
  }

  const lines: string[] = [];

  // Header row
  lines.push('| ' + tableData.headers.join(' | ') + ' |');

  // Separator row
  lines.push('| ' + tableData.headers.map(() => '---').join(' | ') + ' |');

  // Data rows
  for (const row of tableData.rows) {
    lines.push('| ' + row.join(' | ') + ' |');
  }

  return lines.join('\n');
}

/**
 * Format a value for YAML frontmatter
 */
function formatYamlValue(value: any, type?: string): string {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return '';
  }

  // Handle non-string values (numbers, booleans, objects)
  if (typeof value !== 'string') {
    // If it's an array or object, stringify it
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    // For numbers and booleans, convert to string
    return String(value);
  }

  // Handle arrays (as strings)
  if (value.startsWith('[') && value.endsWith(']')) {
    return value;
  }

  // Handle dates
  if (type === 'date' || /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value;
  }

  // Handle numbers (as strings)
  if (type === 'number' || /^-?\d+(\.\d+)?$/.test(value)) {
    return value;
  }

  // Handle booleans (as strings)
  if (value === 'true' || value === 'false') {
    return value;
  }

  // Quote strings that contain special characters
  if (/[:#\[\]{}|>&*!?,]/.test(value) || value.includes('\n')) {
    return `"${value.replace(/"/g, '\\"')}"`;
  }

  return value;
}

/**
 * Sanitize a filename
 */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 100);
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download a single object as Markdown
 */
export async function downloadObjectAsMarkdown(
  objectId: string,
  options: ExportOptions = DEFAULT_OPTIONS
): Promise<void> {
  const markdown = await objectToMarkdown(objectId, options);
  const object = await dataRepository.getObjectById(objectId);
  const fileName = sanitizeFileName(object?.name || 'export') + '.md';

  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, fileName);
}

/**
 * Download multiple objects as a zip
 */
export async function downloadObjectsAsZip(
  objectIds: string[],
  options: ExportOptions = DEFAULT_OPTIONS
): Promise<void> {
  const blob = await exportObjectsToZip(objectIds, options);
  const timestamp = new Date().toISOString().split('T')[0];
  downloadBlob(blob, `pkm-export-${timestamp}.zip`);
}

/**
 * Download all objects as a zip
 */
export async function downloadAllAsZip(
  options: ExportOptions = DEFAULT_OPTIONS
): Promise<void> {
  const blob = await exportAllToZip(options);
  const timestamp = new Date().toISOString().split('T')[0];
  downloadBlob(blob, `pkm-full-export-${timestamp}.zip`);
}
